import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import certificatesService from "../services/certificatesService";
import DateInput from "../components/DateInput";
import { isValidDateUK, fromISODate } from "../utils/dateUtils";
import { useAuth } from "../contexts/AuthContext";
import styles from "./CertificateReviewQueue.module.css";

// DD/MM/YYYY -> YYYY-MM-DD without timezone shifting.
const toISODateSafe = (dateStr) => {
  if (!dateStr) return "";
  const parts = dateStr.split("/");
  if (parts.length !== 3) return dateStr;
  const [day, month, year] = parts;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

const displayDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB");
  } catch {
    return iso;
  }
};

const CertificateReviewQueue = () => {
  const { user } = useAuth();
  const canReview = !!user?.can_review_certificates;
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // detail for the modal
  const [detailLoading, setDetailLoading] = useState(false);
  const [acting, setActing] = useState(false);

  // Editable review fields
  const [editCompleted, setEditCompleted] = useState("");
  const [editExpiration, setEditExpiration] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [showReject, setShowReject] = useState(false);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await certificatesService.getReviewQueue();
      setQueue(res?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load review queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canReview) loadQueue();
  }, [loadQueue, canReview]);

  const openReview = async (row) => {
    setSelected({ ...row, loading: true });
    setDetailLoading(true);
    setShowReject(false);
    setRejectionReason("");
    try {
      const res = await certificatesService.getCertificateForReview(row.id);
      const doc = res?.data || row;
      setSelected(doc);
      setEditCompleted(doc.date_completed ? fromISODate(doc.date_completed) : "");
      setEditExpiration(doc.date_expiration ? fromISODate(doc.date_expiration) : "");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load certificate");
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeReview = () => {
    setSelected(null);
    setShowReject(false);
    setRejectionReason("");
  };

  const handleDownload = async () => {
    if (!selected) return;
    try {
      const result = await certificatesService.downloadCertificate(selected.id);
      const presignedUrl = result?.downloadUrl || result?.url;
      if (presignedUrl) {
        window.open(presignedUrl, "_blank");
      } else if (result instanceof Blob) {
        window.open(window.URL.createObjectURL(result), "_blank");
      } else {
        toast.info("Certificate file is not available for preview");
      }
    } catch (err) {
      toast.error("Failed to open the certificate file");
    }
  };

  const handleApprove = async () => {
    if (!selected) return;
    if (editCompleted && !isValidDateUK(editCompleted))
      return toast.error("Issue date is invalid (DD/MM/YYYY)");
    if (editExpiration && !isValidDateUK(editExpiration))
      return toast.error("Expiry date is invalid (DD/MM/YYYY)");

    setActing(true);
    try {
      const edits = {};
      if (editCompleted) edits.date_completed = toISODateSafe(editCompleted);
      if (editExpiration) edits.date_expiration = toISODateSafe(editExpiration);
      const res = await certificatesService.approveCertificate(selected.id, edits);
      if (res?.success) {
        toast.success("Certificate approved and attached to the property");
        closeReview();
        loadQueue();
      } else {
        throw new Error(res?.message || "Approval failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Approval failed");
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    if (!rejectionReason.trim()) return toast.error("A rejection reason is required");
    setActing(true);
    try {
      const res = await certificatesService.rejectCertificate(
        selected.id,
        rejectionReason.trim()
      );
      if (res?.success) {
        toast.success("Certificate rejected — ticket re-opened for the contractor");
        closeReview();
        loadQueue();
      } else {
        throw new Error(res?.message || "Rejection failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Rejection failed");
    } finally {
      setActing(false);
    }
  };

  if (!canReview) {
    return (
      <div className={styles.container}>
        <div className={styles.state}>
          You do not have access to the certificate review queue.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Certificate Review Queue</h2>
        <button className={styles.refreshBtn} onClick={loadQueue} disabled={loading}>
          Refresh
        </button>
      </div>

      {loading ? (
        <div className={styles.state}>Loading…</div>
      ) : queue.length === 0 ? (
        <div className={styles.state}>No certificates are pending review.</div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Property</th>
                <th>Certificate</th>
                <th>Ticket</th>
                <th>Submitted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {queue.map((row) => (
                <tr key={row.id}>
                  <td>{row.property_name || row.property_public_id || `#${row.property_id}`}</td>
                  <td>{row.type_name || `Type ${row.type_id}`}</td>
                  <td>#{row.ticket_id}</td>
                  <td>{displayDate(row.created_at)}</td>
                  <td>
                    <button className={styles.reviewBtn} onClick={() => openReview(row)}>
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className={styles.overlay} onClick={closeReview}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Review certificate</h3>
              <button className={styles.close} onClick={closeReview}>
                ×
              </button>
            </div>

            {detailLoading ? (
              <div className={styles.state}>Loading…</div>
            ) : (
              <div className={styles.modalBody}>
                <dl className={styles.meta}>
                  <dt>Property</dt>
                  <dd>{selected.property_name || selected.property_public_id || "—"}</dd>
                  <dt>Certificate</dt>
                  <dd>{selected.type_name || `Type ${selected.type_id}`}</dd>
                  <dt>Ticket</dt>
                  <dd>#{selected.ticket_id || selected.source_ticket_id}</dd>
                </dl>

                <button className={styles.downloadBtn} onClick={handleDownload}>
                  Open certificate file
                </button>

                {selected.current_active_certificate && (
                  <div className={styles.superseding}>
                    This will supersede the current active certificate (expires{" "}
                    {displayDate(selected.current_active_certificate.date_expiration)}).
                  </div>
                )}

                <div className={styles.row}>
                  <div className={styles.field}>
                    <label>Issue date</label>
                    <DateInput
                      name="date_completed"
                      value={editCompleted}
                      onChange={(e) => setEditCompleted(e.target.value)}
                      placeholder="DD/MM/YYYY"
                    />
                  </div>
                  <div className={styles.field}>
                    <label>Expiry date</label>
                    <DateInput
                      name="date_expiration"
                      value={editExpiration}
                      onChange={(e) => setEditExpiration(e.target.value)}
                      placeholder="DD/MM/YYYY"
                    />
                  </div>
                </div>

                {showReject && (
                  <div className={styles.field}>
                    <label>Rejection reason (required)</label>
                    <textarea
                      className={styles.reasonInput}
                      rows={3}
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Explain what needs correcting…"
                    />
                  </div>
                )}

                <div className={styles.actions}>
                  {!showReject ? (
                    <>
                      <button
                        className={styles.rejectBtn}
                        onClick={() => setShowReject(true)}
                        disabled={acting}
                      >
                        Reject
                      </button>
                      <button
                        className={styles.approveBtn}
                        onClick={handleApprove}
                        disabled={acting}
                      >
                        {acting ? "Approving…" : "Approve"}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className={styles.cancelBtn}
                        onClick={() => setShowReject(false)}
                        disabled={acting}
                      >
                        Cancel
                      </button>
                      <button
                        className={styles.rejectBtn}
                        onClick={handleReject}
                        disabled={acting}
                      >
                        {acting ? "Rejecting…" : "Confirm rejection"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateReviewQueue;
